/**
 * ErrorScene.ts — Error recovery screen.
 *
 * Displays an error message and offers to return to the title screen.
 *
 * Migrated to FrameRenderer + PanelWriter for framed UI layout.
 */

import type { GameEngine, Scene } from '@core/engine/GameEngine';
import type { MenuOption } from '@ui/MenuSystem';
import { TitleScene } from './TitleScene';

export class ErrorScene implements Scene {
  name = 'ErrorScene';

  private errorMessage: string;

  constructor(errorMessage: string) {
    this.errorMessage = errorMessage;
  }

  async enter(engine: GameEngine): Promise<void> {
    const ctx = engine.getContext();
    const { menu, effects, frameRenderer } = ctx;

    ctx.sprites.hide();
    const layout = frameRenderer.drawFrame({
      title: 'ERROR',
      borderColor: 'red',
      headerColor: 'red',
    });

    const writer = frameRenderer.createWriter(layout.content, effects);
    const offset = writer.calculateVerticalOffset(6);
    writer.skip(offset);
    writer.blankLine();
    writer.writeLine(`  ${this.errorMessage}`, 'white');
    writer.blankLine();
    writer.writeLine('  Your progress has been auto-saved at the last checkpoint.', 'frostSilver');
    writer.blankLine();

    const options: MenuOption[] = [
      { label: 'Return to Title', color: 'amber' },
    ];

    const menuCfg = writer.menuConfig();
    await menu.show(options, {
      showExit: false,
      startRow: menuCfg.startRow,
      startCol: menuCfg.startCol,
      maxWidth: menuCfg.maxWidth,
    });

    ctx.gameState = null;
    await engine.replaceScene(new TitleScene());
  }

  async exit(engine: GameEngine): Promise<void> {
    engine.getContext().sprites.hide();
  }
}
